import { useEffect, useRef, type RefObject } from 'react';

/**
 * Tirer vers le bas — pour relire, ou pour rien.
 *
 * Deux usages du meme geste. Sur le feed, il relit : rien n'ecoute la
 * base en continu, et c'est le geste que tout le monde essaie deja.
 * Ailleurs, il ne fait qu'etirer — le contenu suit le doigt puis revient.
 *
 * Cette seconde forme n'est pas un ornement. Sans elle, tirer sur une
 * liste ne donnerait rien du tout, et un ecran qui ne repond pas au doigt
 * passe pour fige. Elle rend la matiere sans promettre une mise a jour
 * qui n'aurait pas lieu.
 *
 * Rien ne passe par React pendant le geste. C'est la difference entre
 * fluide et poussif : une position d'etat, c'est un rendu de l'arbre a
 * chaque image, soixante fois par seconde, pour deplacer un seul noeud.
 * On ecrit donc directement sur son style, dans une image d'animation, et
 * React ne revoit rien tant que le doigt est pose.
 *
 * Le geste n'existe qu'en haut de la zone : ailleurs, tirer vers le bas
 * veut dire remonter la liste. Et il se retire des que le doigt part plus
 * a l'horizontale qu'a la verticale, pour laisser le changement d'onglet
 * prendre la main — les deux gestes commencent au meme endroit.
 */

/**
 * Au-dela, lacher declenche. En deca, tout revient en place.
 *
 * Cinquante-six pixels de contenu, soit environ quatre-vingts de doigt
 * une fois la resistance appliquee — la course a laquelle l'iPhone a
 * habitue le pouce.
 */
const DECLENCHE_PX = 56;
/** Le contenu ne descend jamais plus loin, quoi qu'on fasse. */
const MAX_PX = 110;
/** Distance a franchir depuis la pose avant de juger la direction du doigt. */
const ENGAGE_PX = 8;
/** Classe posee le temps du geste : elle coupe la transition de retour. */
const TIRE = 'pull-held';
/**
 * Ce que le contenu garde d'ecart pendant la relecture : de quoi montrer
 * la roue qui tourne dans le creux, et pas un pixel de plus.
 */
const TENU_PX = 46;
/** Duree de la pose dans le creux, alignee sur la feuille de style. */
const POSE_MS = 340;
/**
 * Classe de la glissade vers le creux.
 *
 * Le contenu s'y posait en un tiers de seconde, puis restait immobile
 * jusqu'a la fin de la relecture — et c'est cette immobilite, la roue
 * tournant au-dessus d'une liste figee, qui se lisait comme un blocage.
 * Il y glisse maintenant sur plus d'une seconde, en ralentissant sans
 * cesse : pendant que la roue tourne, la liste bouge encore. C'est ce que
 * fait Instagram, mesure image par image sur sa video.
 */
const GLISSE = 'pull-glisse';
/** Classe du retour a sa place, une fois la relecture faite. */
const REVIENT = 'pull-revient';
/** Classe de la roue pendant la relecture : elle reste pleine et tourne. */
const TOURNE = 'tirette--tourne';
/** Classe qui met la roue en marche, au seuil ou lacher relit. */
const ANIME = 'tirette--anime';
/**
 * Le creux a partir duquel la roue se voit en entier : sa hauteur.
 *
 * Les rais paraissaient des le premier pixel, alors que la roue etait
 * encore sous le contenu : sur les evenements, la barre de recherche
 * recouvrait les premiers. Ils ne commencent donc qu'ici, et finissent au
 * meme seuil.
 */
const ROUE_PX = 22;

/*
  Le rebond d'une liste lancee qui touche le haut.

  Les onglets n'ont pas le rebond du systeme — il emportait
  l'illustration avec lui —, si bien qu'une liste lancee vers le haut
  s'arretait net en y arrivant, comme contre un mur. On le refait ici : le
  contenu descend d'autant plus loin qu'il arrivait vite, decouvre le
  debut de la roue, et remonte. Jamais jusqu'au seuil : un rebond ne
  relit rien.

  La course suit la reponse d'un ressort amorti au plus juste,
  x(t) = v · t · e^(−t/τ) : elle part a la vitesse exacte ou le contenu
  arrivait — c'est ce qui evite de sentir le passage du defilement au
  rebond —, culmine au bout de τ, et revient sans osciller. Plus la liste
  arrive vite, plus τ raccourcit, pour que le sommet reste sous le
  plafond : un rebond vif et court, comme sur l'iPhone.

  Le contenu est anime par le moteur d'animation du navigateur, pas image
  par image : il se deroule dans le compositeur, meme quand la page est
  occupee ailleurs. Ecrit a la main a chaque image, il sautait des qu'une
  image manquait — le petit blocage qu'on sentait.
*/
/**
 * Le rebond le plus fort de la liste. Plafonne bas, un lancer vif devait
 * tenir dans un ressort de quelques centiemes de seconde : mesure sur
 * l'iPhone, une liste arrivee a 6,7 px/ms s'arretait en vingt-quatre
 * millisecondes, un claquement. Un lancer fort va ainsi jusqu'a la roue
 * complete ; elle ne tourne jamais pour un rebond.
 */
const REBOND_MAX_PX = 90;
/*
  Le haut de l'ecran rebondit aussi, mais court.

  Pendant un lancer, tout monte d'un bloc — l'illustration, les
  commandes, la liste. Le rebond ne faisait continuer que la liste :
  l'illustration s'arretait net au choc pendant que la liste filait
  encore, et c'est ce decrochage qu'on sentait, mesure a l'appui (les
  vitesses, elles, se raccordaient). Le haut repart donc lui aussi a la
  vitesse d'arrivee, et s'amortit vite ; la liste s'amortit plus loin, et
  l'ecart entre les deux ouvre le creux ou parait la roue.
*/
/** Le temps que met le haut a culminer, au plus. */
const HAUT_TAU_MS = 35;
/** Jusqu'ou le haut descend, au plus. */
const HAUT_MAX_PX = 18;
/**
 * Le retour, plus lent que la montee.
 *
 * La montee part a la vitesse d'arrivee, et c'est elle qui fixe sa
 * duree : la ralentir, c'etait recasser le raccord. Le retour, lui, part
 * du sommet a l'arret ; on peut le prendre plus lentement sans rien
 * casser. D'abord 1,4 fois la montee, puis 2 : la roue qui disparait sous
 * le fil qui remonte doit se laisser voir partir.
 */
const RETOUR = 2;

/**
 * Un ressort qui monte et redescend.
 *
 * La montee est la reponse d'un ressort amorti au plus juste,
 * x = A·(t/τ)·e^(1 − t/τ), qui part a la vitesse A·e/τ ; au sommet, sa
 * vitesse est nulle, et le retour repart de la, x = A·(1 + s)·e^(−s),
 * s = (t − τ)/τr. Les deux se raccordent sans cassure, position et
 * vitesse.
 */
function ressort(sommet: number, montee: number, retour: number) {
  return (t: number) =>
    t <= montee
      ? sommet * (t / montee) * Math.exp(1 - t / montee)
      : sommet * (1 + (t - montee) / retour) * Math.exp(-(t - montee) / retour);
}
/** Le temps qu'il met a culminer, au plus. */
const REBOND_TAU_MS = 95;
/** Classe posee quand une liste lancee approche du haut : sa couche est prete. */
const PRET = 'pull-pret';
/**
 * En dessous, en px/ms, la liste arrive en haut sans elan : pas de rebond.
 *
 * Il etait a 0,35 : un lancer doux s'arretait encore net contre le haut.
 * Un ressort, meme petit, doit repondre a tout ce qui arrive lance ; seul
 * ce qui se pose presque immobile n'en a pas.
 */
const REBOND_VITESSE_MIN = 0.08;
/** Le plus petit rebond : assez pour se sentir, meme lance tout doucement. */
const REBOND_MIN_PX = 8;

/*
  Le rebond part a l'instant ou la liste touche le haut, pas apres.

  La page n'apprend l'arrivee qu'au defilement suivant, et le rebond met
  encore une image ou deux a gagner l'ecran : filme dans le simulateur,
  la liste restait plantee contre le haut 58 ms — trois ou quatre images —
  avant de repartir. C'est ce temps mort qu'on voyait entre le defilement
  et la roue ; sur la video d'Instagram, il n'existe pas.

  Or l'arrivee se prevoit. Une liste lancee freine selon une loi connue,
  celle de l'iPhone : sa vitesse perd 0,2 % par milliseconde. Au dernier
  defilement avant le haut, on sait donc dans combien de temps elle y
  sera, et a quelle vitesse. Le rebond est pose a ce moment-la, en
  avance : le compositeur le declenche a l'heure, et prend le relais du
  defilement sans le moindre arret.
*/
/** Le freinage d'une liste lancee, par milliseconde : `decelerationRate` normal de l'iPhone. */
const FREIN = -Math.log(0.998);
/**
 * Jusqu'a quelle distance dans le temps on prevoit l'arrivee.
 *
 * Le rebond se pose quand il reste au plus deux images et un peu plus
 * avant son depart : les defilements arrivent toutes les 17 ms, mais il
 * en manque parfois un juste avant le haut — releve dans le simulateur,
 * 33 ms entre les deux derniers. Plus tot, on attend le suivant, plus
 * proche donc plus juste.
 */
const PREVISION_MS = 40;
/**
 * L'avance a prendre sur l'arrivee.
 *
 * Le rebond pose a l'heure exacte partait deux images trop tard : la
 * position qu'on lit a deja du retard sur l'ecran, et l'animation met
 * encore le temps d'une transmission a gagner le compositeur. Filme dans
 * le simulateur : 33 ms d'avance, il partait un rien trop tot ; 25, il
 * prend la suite au moment ou la liste touche.
 */
const AVANCE_MS = 25;
/**
 * La vitesse d'une prevision se prend sur au moins ce temps.
 *
 * D'un defilement au suivant, l'ecart trompe : juste apres le lacher,
 * deux defilements sont arrives a 6 ms l'un de l'autre et 153 px
 * d'ecart — 25 px/ms, quatre fois la vraie vitesse —, et le rebond
 * partait alors que la liste etait encore a 400 px du haut. Sur deux
 * images, les a-coups s'effacent.
 */
const FENETRE_MS = 30;

/**
 * La resistance.
 *
 * Sans elle, le contenu suit le doigt au pixel pres et l'ecran parait
 * decroche. En freinant a mesure qu'on descend, le geste retrouve la
 * matiere d'un elastique : on sent qu'on tire sur quelque chose.
 */
function freiner(distance: number): number {
  return MAX_PX * (1 - Math.exp(-distance / MAX_PX));
}

/** Le meme geste, par l'autre bout ou sous condition. */
export interface OptionsDuTirage {
  /**
   * Le bord ou le geste se tient. `haut` par defaut ; `bas` pour une
   * conversation, qui se lit par le bas : tirer vers le haut, une fois
   * en bas, l'etire, et une liste lancee qui touche le bas rebondit.
   * Tout le reste est le meme geste, au signe pres.
   */
  bord?: 'haut' | 'bas';
  /**
   * Ne rebondir que sur l'elan d'un doigt.
   *
   * Une conversation defile aussi toute seule : elle se recale en bas a
   * l'arrivee d'un message, et y glisse apres un envoi. Lues comme un
   * lancer, ces descentes la faisaient rebondir sans que personne n'ait
   * rien lance. Le rebond n'est donc permis qu'apres un doigt leve sur
   * la zone, jusqu'au prochain contact ailleurs.
   */
  elanDuDoigt?: boolean;
  /**
   * La zone ne bouge jamais, seul le contenu.
   *
   * Dans un onglet, le haut de l'ecran rebondit un peu avec la liste.
   * Dans une conversation, la zone est celle qui defile : la deplacer
   * emportait sa propre limite, et le contenu restait rogne a l'ancienne,
   * net, au-dessus du composeur. Seul le contenu se deplace alors, dans
   * une zone immobile.
   */
  zoneFixe?: boolean;
}

/** Au-dela, un elan est retombe : ce qui defile ensuite n'est plus a lui. */
const ELAN_MS = 3000;

export function usePullToRefresh(
  /* Les deux peuvent etre vides : le crochet ne fait rien sans la zone. */
  zone: RefObject<HTMLElement | null>,
  corps: RefObject<HTMLElement | null>,
  /** Absente : le geste etire, et rien de plus. */
  onRefresh?: () => void | Promise<unknown>,
  /** La roue qui parait dans le creux. Absente : le geste ne montre rien. */
  jauge?: RefObject<HTMLElement | null>,
  options: OptionsDuTirage = {},
): void {
  /* Le rappel est fige a la pose des ecouteurs : le lire par reference
     evite de les reposer a chaque rendu du parent. */
  const refreshRef = useRef(onRefresh);
  refreshRef.current = onRefresh;

  useEffect(() => {
    const el = zone.current;
    if (!el) return;

    /*
      Le sens du bord : 1 en haut, -1 en bas. Tout ce qui se mesure vers
      le bord le multiplie — le doigt, la vitesse, le deplacement du
      contenu. En haut il vaut 1, et le geste est celui qu'il a toujours
      ete, au calcul pres.
    */
    const bas = options.bord === 'bas';
    const s = bas ? -1 : 1;
    /** Ce qu'il reste de liste avant le bord. */
    const avantLeBord = () =>
      bas ? el.scrollHeight - el.clientHeight - el.scrollTop : el.scrollTop;
    /*
      Pas de haut d'ecran a faire rebondir a part : la zone est fixe, ou
      elle ne fait qu'un avec le contenu — et deux animations sur un meme
      noeud s'ecraseraient.
    */
    const unSeul = () => Boolean(options.zoneFixe) || corps.current === el;
    /* L'elan d'un doigt leve sur la zone, encore en cours. */
    let lance = !options.elanDuDoigt;
    let lanceJusqua: ReturnType<typeof setTimeout> | null = null;

    /* Le point de pose, jamais deplace : lui seul juge la direction. */
    let origine: { x: number; y: number } | null = null;
    let vertical = false;
    /* D'ou l'etirement se mesure. Suit le doigt tant qu'on n'etire pas. */
    let base = 0;
    /* Le doigt au deplacement d'avant : c'est lui que `scrollTop` decrit. */
    let precedent = 0;
    /* Le rebond du systeme fait l'etirement a notre place. */
    let natif = false;
    let engage = false;
    let enCours = false;
    let vivant = true;
    let distance = 0;
    let image = 0;
    let differe: ReturnType<typeof setTimeout> | null = null;
    /* Un doigt est pose : ce qui defile est a lui, pas a l'elan. */
    let doigtPose = false;
    /* La vitesse de la liste, lissee sur les derniers defilements. */
    let dernierHaut = el.scrollTop;
    let dernierInstant = performance.now();
    let vitesse = 0;
    /* Les derniers defilements, pour une vitesse prise sur plusieurs images. */
    const traces: { t: number; y: number }[] = [];
    /* L'image de la roue pendant le rebond, et l'animation du contenu. */
    let rebond = 0;
    let animation: Animation | null = null;
    let animationHaut: Animation | null = null;
    let pretJusqua: ReturnType<typeof setTimeout> | null = null;

    /**
     * Une seule ecriture de style par image, jamais une par evenement.
     *
     * L'image lit `distance` au moment ou elle s'execute, et non la
     * valeur qu'on lui avait passee : deux ou trois deplacements arrivent
     * entre deux images, et poser le premier revenait a afficher une
     * position deja perimee. C'est exactement ce retard qu'on sentait
     * sous le doigt.
     */
    function poser(y: number) {
      distance = y;
      if (image) return;
      image = requestAnimationFrame(() => {
        image = 0;
        const noeud = corps.current;
        if (noeud) noeud.style.transform = distance ? `translate3d(0,${s * distance}px,0)` : '';
        peindreLaRoue();
      });
    }

    /**
     * La part du chemin parcourue, portee a la roue.
     *
     * Dans la meme image que le contenu : deux ecritures de style separees,
     * c'est la roue en retard d'une image sur le creux qui la decouvre.
     */
    function peindreLaRoueA(valeur: number, tourner = true) {
      const roue = jauge?.current;
      if (!roue) return;
      const part = (valeur - ROUE_PX) / (DECLENCHE_PX - ROUE_PX);
      roue.style.setProperty('--p', String(Math.min(Math.max(part, 0), 1)));
      /* Le creux, pour que la roue s'y tienne au milieu. */
      roue.style.setProperty('--creux', `${valeur}px`);
      /*
        Elle ne tourne que si lacher va relire.

        Elle tournait des le premier pixel : un petit tirage la mettait en
        marche, on lachait, et rien ne se relisait — la roue avait promis
        quelque chose qui n'arrivait pas. Elle se dessine donc a mesure
        qu'on tire, immobile, et ne se met a tourner qu'au seuil exact ou
        le lacher declenche la relecture. Remonter sous le seuil l'arrete.

        Le meme `DECLENCHE_PX`, compare a la meme `distance` que dans
        `onEnd` : tourner et relire sont une seule et meme condition, et
        ne peuvent pas se contredire.
      */
      /* Ou rien ne se relit, elle ne se met jamais a tourner : elle ne
         promet pas ce qui n'aura pas lieu. */
      roue.classList.toggle(
        ANIME,
        tourner && Boolean(refreshRef.current) && valeur >= DECLENCHE_PX,
      );
    }

    /** Le rebond s'arrete, et le contenu reprend sa place. */
    function finirLeRebond() {
      if (!rebond && !animation) return;
      if (rebond) cancelAnimationFrame(rebond);
      rebond = 0;
      animation?.cancel();
      animation = null;
      animationHaut?.cancel();
      animationHaut = null;
      distance = 0;
      corps.current?.classList.remove(PRET);
      if (!unSeul()) el?.classList.remove(PRET);
      peindreLaRoueA(0);
    }

    /**
     * La liste lancee touche le haut a `elan` px/ms : elle rebondit.
     *
     * Le contenu part tout de suite, dans l'evenement meme : attendre
     * l'image suivante le laissait arrete une image contre le haut. La
     * roue, elle, se dessine image par image sur la meme course.
     */
    function rebondir(elan: number, delai = 0) {
      const noeud = corps.current;
      if (!noeud) return;
      const tau = Math.min(REBOND_TAU_MS, (REBOND_MAX_PX * Math.E) / elan);
      /*
        Le sommet vaut v·τ/e, et jamais moins que le plus petit rebond :
        un lancer doux part alors un peu plus vite qu'il n'arrivait, mais
        a ces vitesses la difference ne se voit pas, et le ressort, lui,
        se sent.
      */
      const sommet = Math.max((elan * tau) / Math.E, REBOND_MIN_PX);
      const course = ressort(sommet, tau, tau * RETOUR);
      /* Le haut : meme vitesse de depart, ressort plus court. */
      const tauHaut = Math.min(HAUT_TAU_MS, tau, (HAUT_MAX_PX * Math.E) / elan);
      const haut = ressort((elan * tauHaut) / Math.E, tauHaut, tauHaut * RETOUR);
      /* La liste bouge dans le haut qui bouge : sa part a elle est l'ecart.
         Seule, elle fait toute la course. */
      const seul = unSeul();
      const ecart = (t: number) => (seul ? course(t) : Math.max(0, course(t) - haut(t)));

      /* Huit retours apres le sommet : il ne reste pas un demi-pixel. */
      const duree = tau + 8 * tau * RETOUR;
      const images: Keyframe[] = [];
      const imagesHaut: Keyframe[] = [];
      for (let k = 0; k <= 64; k++) {
        const t = (duree * k) / 64;
        images.push({ transform: `translate3d(0,${s * ecart(t)}px,0)` });
        imagesHaut.push({ transform: `translate3d(0,${s * haut(t)}px,0)` });
      }
      images[64] = { transform: 'translate3d(0,0,0)' };
      imagesHaut[64] = { transform: 'translate3d(0,0,0)' };
      noeud.classList.add(PRET);
      if (!seul) el?.classList.add(PRET);
      /* En attendant son heure, il ne touche a rien : la liste defile encore. */
      const quand = { duration: duree, delay: delai, easing: 'linear' };
      animation = noeud.animate(images, quand);
      animationHaut = seul ? null : (el?.animate(imagesHaut, quand) ?? null);
      animation.onfinish = finirLeRebond;

      const depart = performance.now() + delai;
      const pas = () => {
        distance = ecart(performance.now() - depart);
        /* Jusqu'a la roue complete, mais elle ne tourne pas : un rebond
           ne relit rien. */
        peindreLaRoueA(distance, false);
        rebond = requestAnimationFrame(pas);
      };
      pas();
    }

    /**
     * Une liste lancee vers le haut, pres d'y arriver : sa couche est
     * preparee avant le choc. La demander au compositeur au moment du
     * rebond, c'est une image perdue, et c'est celle que l'oeil juge.
     */
    function preparer() {
      corps.current?.classList.add(PRET);
      if (!unSeul()) el?.classList.add(PRET);
      if (pretJusqua) clearTimeout(pretJusqua);
      pretJusqua = setTimeout(() => {
        pretJusqua = null;
        if (animation) return;
        corps.current?.classList.remove(PRET);
        if (!unSeul()) el?.classList.remove(PRET);
      }, 250);
    }

    /**
     * Suivre l'elan de la liste, pour savoir a quelle vitesse elle touche
     * le haut.
     *
     * Le dernier defilement avant le haut est souvent tronque — la liste
     * s'arrete a zero au milieu d'une image —, et sous-estime la vitesse :
     * on garde la plus forte des deux dernieres mesures.
     */
    function onScroll() {
      if (!el) return;
      const maintenant = performance.now();
      const ecoule = maintenant - dernierInstant;
      const haut = el.scrollTop;
      const avant = vitesse;
      traces.push({ t: maintenant, y: haut });
      if (traces.length > 8) traces.shift();
      /*
        Deux evenements presque ensemble ne font pas une mesure : quelques
        pixels sur un dixieme de milliseconde donnaient une vitesse enorme,
        et un lancer doux rebondissait comme un lancer vif. En dessous de
        4 ms, on attend le suivant pour mesurer.
      */
      let mesure = 0;
      if (ecoule >= 4) {
        mesure = (haut - dernierHaut) / ecoule;
        vitesse = ecoule > 120 ? mesure : 0.6 * mesure + 0.4 * vitesse;
        dernierHaut = haut;
        dernierInstant = maintenant;
      }

      if (doigtPose || enCours || animation || !lance) return;
      const elan = Math.max(-s * vitesse, -s * avant, -s * mesure);
      /* Un rien de bord, en bas : la hauteur se lit en fractions de pixel. */
      const reste = avantLeBord();
      if (reste > (bas ? 0.5 : 0)) {
        if (elan >= REBOND_VITESSE_MIN && reste < 1200) {
          preparer();
          prevoir(reste, vitesseEtablie(maintenant, haut));
        }
        return;
      }
      /* Le systeme rebondit deja la ou la feuille de style le lui laisse. */
      if (getComputedStyle(el).overscrollBehaviorY !== 'none') return;
      if (elan >= REBOND_VITESSE_MIN) rebondir(elan);
    }

    /**
     * La vitesse vers le haut, en px/ms, depuis le plus recent des
     * defilements vieux d'au moins `FENETRE_MS`. Zero s'il n'y en a pas.
     */
    function vitesseEtablie(maintenant: number, haut: number): number {
      for (let i = traces.length - 1; i >= 0; i--) {
        const ecart = maintenant - traces[i].t;
        if (ecart >= FENETRE_MS) return (s * (traces[i].y - haut)) / ecart;
      }
      return 0;
    }

    /**
     * La liste est a `reste` pixels du haut, et monte a `v` px/ms : si
     * son rebond doit partir dans les deux prochaines images, il est pose
     * des maintenant, a l'heure de l'arrivee.
     */
    function prevoir(reste: number, v: number) {
      if (!el || v <= 0) return;
      if (getComputedStyle(el).overscrollBehaviorY !== 'none') return;
      const part = (FREIN * reste) / v;
      // Elle s'arretera avant le haut : rien a prevoir.
      if (part >= 1) return;
      const dans = -Math.log(1 - part) / FREIN;
      if (dans - AVANCE_MS > PREVISION_MS) return;
      const arrivee = v - FREIN * reste;
      if (arrivee < REBOND_VITESSE_MIN) return;
      rebondir(arrivee, Math.max(0, dans - AVANCE_MS));
    }

    function peindreLaRoue() {
      peindreLaRoueA(distance);
    }

    /**
     * Le systeme etire a notre place : rien ne bouge dans le document, et
     * la roue n'a donc pas de creux ou paraitre. On la tient eteinte.
     */
    function mesurerSansPeindre(y: number) {
      distance = y;
    }

    function relacher(vers: number) {
      if (image) cancelAnimationFrame(image);
      image = 0;
      distance = vers;
      const noeud = corps.current;
      if (!noeud) return;
      // La transition reprend ses droits des que la classe tombe.
      noeud.classList.remove(TIRE);
      noeud.style.transform = vers ? `translate3d(0,${s * vers}px,0)` : '';
    }

    function onStart(event: TouchEvent) {
      doigtPose = true;
      if (options.elanDuDoigt) lance = false;
      /* Reprendre la liste en plein rebond : elle revient sous le doigt. */
      finirLeRebond();
      if (enCours || event.touches.length !== 1) return;
      const doigt = event.touches[0];
      if (!el) return;
      /*
        Le doigt est suivi meme s'il se pose au milieu de la liste.

        On ne prend la main qu'une fois le haut atteint — mais il faut
        l'avoir suivi jusque-la pour savoir ou il en est. Attendre un
        nouveau contact, c'etait laisser WebKit rebondir sur le haut a la
        fin de chaque remontee, et l'illustration partait avec lui.
      */
      origine = { x: doigt.clientX, y: doigt.clientY };
      vertical = false;
      base = doigt.clientY;
      precedent = doigt.clientY;
      engage = false;
      /*
        L'etirement du systeme, quand la zone le permet.

        Le notre prend le relais du defilement au moment ou le haut est
        atteint, et ce relais se sent : le defilement suit le doigt dans le
        compositeur, a la cadence de l'ecran ; l'etirement passe par une
        image d'animation de la page, une ou deux images plus tard et
        souvent a moitie cadence. Sur une liste qui monte d'un bloc jusqu'au
        tirage, c'est un changement qu'on remarque. Le rebond du systeme,
        lui, est le meme geste que le defilement : il n'y a plus de relais.

        C'est la feuille de style qui en decide — voir
        `.sub-screen__body--tirable`. Le crochet ne fait alors que mesurer
        le doigt, pour savoir au lacher s'il faut relire. Une liste trop
        courte pour defiler ne rebondit pas : elle garde notre etirement.
      */
      natif =
        getComputedStyle(el).overscrollBehaviorY !== 'none' &&
        el.scrollHeight > el.clientHeight + 1;
      /*
        La couche est preparee des la pose du doigt, pas au premier
        deplacement : demander au compositeur en pleine course, c'est une
        image perdue, et c'est celle ou l'oeil juge si l'ecran suit.
      */
      if (!natif) corps.current?.classList.add(TIRE);
    }

    function onMove(event: TouchEvent) {
      const de = origine;
      const doigt = event.touches[0];
      if (!de || !doigt || !el) return;
      const y = doigt.clientY;
      const avant = precedent;
      precedent = y;

      if (!vertical) {
        const dx = doigt.clientX - de.x;
        const dy = y - de.y;
        /*
          La direction se juge une fois, sur tout le chemin depuis la pose.

          Elle se jugeait a chaque deplacement, sur les derniers pixels
          seulement : remonter la liste jusqu'en haut puis marquer un temps,
          c'etait laisser le doigt trembler d'un pixel de cote pour zero en
          hauteur, et le geste se croyait horizontal. Il abandonnait pour
          toute la duree du contact — tirer ne donnait plus rien tant qu'on
          n'avait pas leve le doigt.
        */
        if (Math.abs(dx) < ENGAGE_PX && Math.abs(dy) < ENGAGE_PX) return;
        // Plutot horizontal : c'est un changement d'onglet, on s'efface.
        if (Math.abs(dx) > Math.abs(dy)) {
          origine = null;
          return;
        }
        vertical = true;
        /* Les pixels du jugement ne comptent pas : sans cela, le contenu
           sauterait d'autant au premier etirement. */
        base = y;
      }

      if (!engage) {
        /*
          Pas encore en haut : la liste defile sous le doigt, au pixel pres.
          La base est donc la ou le doigt touchera le haut — sa position,
          plus ce qu'il reste de liste au-dessus.

          Elle suivait le doigt, et l'etirement ne partait qu'apres huit
          pixels de plus une fois le haut atteint. Remonter la liste d'un
          seul geste, c'etait donc la voir s'arreter net en haut pendant
          que le doigt continuait, puis repartir : un a-coup, juste entre
          la liste et le tirage. Mesuree ainsi, la base fait de l'etirement
          la suite exacte du defilement.

          La position lue est celle d'avant ce deplacement-ci : le
          navigateur ne fait defiler qu'apres nous avoir prevenus. Le reste
          se compte donc depuis le doigt precedent — depuis l'actuel, le
          contenu restait fige un deplacement de plus.

          Moins d'un pixel compte pour le haut : un defilement lance peut
          s'y arreter sur une fraction.
        */
        const reste = avantLeBord();
        if (reste >= 1) {
          base = avant + s * reste;
          return;
        }
        // Au bord, le doigt repart vers la liste : rien ne bouge, la base le suit.
        if (s * (y - base) <= 0) {
          base = y;
          return;
        }
        engage = true;
      }

      const tire = s * (y - base);
      /*
        Le doigt est repasse au-dessus de l'endroit ou l'etirement a
        commence : ce n'est plus un tirage. On rend la main a la liste, et
        redescendre en haut le reprend aussitot, sans lever le doigt.
      */
      if (tire <= 0) {
        engage = false;
        base = y;
        if (natif) distance = 0;
        else poser(0);
        return;
      }

      /* Le systeme etire : on retient seulement jusqu'ou, pour le lacher.
         Le seuil reste celui du doigt, le meme effort que partout ailleurs. */
      if (natif) {
        mesurerSansPeindre(freiner(tire));
        return;
      }

      /*
        A partir d'ici le navigateur ne doit plus rien faire de ce geste :
        sans cela, WebKit ajoute son propre rebond elastique — celui qui
        emporte l'illustration — et l'ecran part deux fois. L'ecouteur est
        pose en `passive: false` pour que ce refus soit accepte. Un geste
        dont le defilement a deja commence ne se refuse plus : on ne le
        demande alors pas.
      */
      if (event.cancelable) event.preventDefault();
      poser(freiner(tire));
    }

    function onEnd() {
      doigtPose = false;
      if (options.elanDuDoigt) {
        lance = true;
        if (lanceJusqua) clearTimeout(lanceJusqua);
        lanceJusqua = setTimeout(() => {
          lance = false;
        }, ELAN_MS);
      }
      const tire = engage;
      origine = null;
      vertical = false;
      engage = false;
      // La couche n'a plus lieu d'etre tenue, geste reconnu ou non.
      if (!tire) return corps.current?.classList.remove(TIRE);

      // Sans rappel, le geste n'etait qu'un etirement : il revient.
      if (!refreshRef.current || distance < DECLENCHE_PX || enCours) {
        peindreLaRoueA(0);
        corps.current?.classList.remove(GLISSE);
        corps.current?.classList.add(REVIENT);
        return relacher(0);
      }

      /*
        Le contenu se pose dans le creux, sous la roue qui tourne, le temps
        de la relecture. Puis il remonte — en rebondissant.

        La descente dans le creux se fait en douceur. La relecture ne part
        qu'une fois le contenu pose : une animation et une recomposition
        de la liste sur les memes images, c'est une saccade.

        En mode natif il n'y a pas de creux a tenir — le systeme a deja
        tout ramene — et la roue n'a pas ete montree : on relit sans elle.
      */
      enCours = true;
      const roue = natif ? null : jauge?.current;
      corps.current?.classList.remove(REVIENT);
      corps.current?.classList.add(GLISSE);
      relacher(natif ? 0 : TENU_PX);
      if (roue) {
        roue.style.setProperty('--p', '1');
        /* Elle suit le contenu jusqu'au creux tenu, du meme pas que lui. */
        roue.style.setProperty('--creux', `${TENU_PX}px`);
        roue.classList.add(ANIME, TOURNE);
      }

      differe = setTimeout(() => {
        differe = null;
        if (!vivant) return;
        void Promise.resolve(refreshRef.current?.())
          .catch(() => undefined)
          .then(() => {
            enCours = false;
            if (!vivant) return;
            /*
              Le contenu reprend sa glissade jusqu'a sa place, la ou il
              en est — pas depuis le creux : si la relecture a ete
              rapide, il n'y est pas encore arrive, et repartir de 46 px
              ferait un saut. La roue s'arrete au moment ou il repart,
              et c'est lui qui la range en la recouvrant.
            */
            corps.current?.classList.remove(GLISSE);
            corps.current?.classList.add(REVIENT);
            relacher(0);
            roue?.classList.remove(TOURNE, ANIME);
          });
      }, POSE_MS);
    }

    /* Un contact ailleurs — le bouton d'envoi, le champ — coupe l'elan :
       ce qui defile ensuite est l'oeuvre de l'ecran, pas du doigt. */
    const contactAilleurs = () => {
      lance = false;
    };
    if (options.elanDuDoigt) {
      document.addEventListener('touchstart', contactAilleurs, { passive: true, capture: true });
    }
    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('scroll', onScroll, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd, { passive: true });
    el.addEventListener('touchcancel', onEnd, { passive: true });

    return () => {
      vivant = false;
      if (differe) clearTimeout(differe);
      if (image) cancelAnimationFrame(image);
      if (rebond) cancelAnimationFrame(rebond);
      animation?.cancel();
      animationHaut?.cancel();
      if (pretJusqua) clearTimeout(pretJusqua);
      if (lanceJusqua) clearTimeout(lanceJusqua);
      document.removeEventListener('touchstart', contactAilleurs, { capture: true });
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('scroll', onScroll);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, [zone, corps]);
}
